import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import logoAr from "../../assets/images/logoAr.webp";
import { IoMdLock, IoMdEye, IoMdEyeOff } from "react-icons/io";
import PhoneInput from "../../components/ui/PhoneInput";
import "react-phone-number-input/style.css";
import { useLogin } from "../../hooks/useAuth";
import { setTokens, getTokens } from "@/utils/token";

const loginSchema = z.object({
  mobile: z
    .string()
    .regex(/^\d{9,10}$/, "يرجى إدخال رقم جوال صحيح مكوّن من 9 إلى 10 أرقام"),
  password: z.string().min(8, "كلمة المرور يجب أن تكون 8 أحرف على الأقل"),
});

type LoginFormData = z.infer<typeof loginSchema>;

const Login = () => {
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    setValue,
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    mode: "onChange",
  });

  const loginMutation = useLogin();

  const [logoAnimated, setLogoAnimated] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formVisible, setFormVisible] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  // Redirect if already logged in
  useEffect(() => {
    const { access: token } = getTokens();
    if (token) navigate("/");
  }, [navigate]);

  // Load saved phone number if "remember me" was selected
  useEffect(() => {
    const savedPhone = localStorage.getItem("rememberedPhone");
    if (savedPhone) {
      setValue("mobile", savedPhone);
      setRememberMe(true);
    }
  }, [setValue]);

  useEffect(() => {
    setTimeout(() => setLogoAnimated(true), 400);
    setTimeout(() => setFormVisible(true), 1100);
  }, []);

  const onSubmit = async (data: LoginFormData) => {
    try {
      setErrorMsg("");

      // تسجيل الدخول
      const res = await loginMutation.mutateAsync({
        phone: data.mobile,
        password: data.password,
      });

      // تنظيف أي بيانات قديمة من جلسة سابقة
      localStorage.removeItem("sessionStarted");

      // حفظ التوكنات
      setTokens(res.access, res.refresh);

      // حفظ الهاتف إذا اختار تذكرني
      if (rememberMe) {
        localStorage.setItem("rememberedPhone", data.mobile);
      } else {
        localStorage.removeItem("rememberedPhone");
      }

      // الانتقال للصفحة
      navigate("/start-session");
    } catch (error) {
      setErrorMsg("بيانات الدخول غير صحيحة، تأكد من الرقم وكلمة المرور.");
    }
  };

  return (
    <div className='min-h-screen flex flex-col items-center justify-center p-2'>
      <div
        className={`transition-all duration-700 mt-4 flex flex-col items-center ${
          logoAnimated ? "scale-75" : "scale-150 translate-y-40"
        }`}
        style={{ minHeight: 120 }}
      >
        <img
          src={logoAr}
          alt='نيتلوس'
          className='w-64 max-w-xs mt-2'
          draggable='false'
        />
      </div>

      <div
        className={`
          w-full max-w-md bg-white/95 rounded-2xl p-6 flex flex-col items-center
          transition-all duration-700 ease-in-out
          ${
            formVisible
              ? "opacity-100 translate-y-0"
              : "opacity-0 translate-y-10"
          }
        `}
      >
        <h2 className='text-4xl font-bold text-red-700 text-center mb-2'>
          مرحباً بعودتك!
        </h2>
        <p className='text-gray-700 text-center mb-6'>
          قم بتسجيل الدخول إلى حسابك الحالي
        </p>

        <form className='w-full space-y-5' onSubmit={handleSubmit(onSubmit)}>
          {/* Mobile Number */}
          <PhoneInput
            control={control}
            name='mobile'
            error={errors.mobile?.message}
            label='رقم الجوال'
          />

          {/* Password */}
          <div>
            <label className='block text-right text-gray-700 mb-1'>
              كلمة المرور
            </label>
            <div className='flex items-center border rounded-lg overflow-hidden bg-gray-100'>
              <button
                type='button'
                onClick={() => setShowPassword((prev) => !prev)}
                className='px-2 text-gray-600 focus:outline-none'
                tabIndex={-1}
              >
                {showPassword ? (
                  <IoMdEyeOff className='w-5 h-5' />
                ) : (
                  <IoMdEye className='w-5 h-5' />
                )}
              </button>
              <input
                {...register("password")}
                type={showPassword ? "text" : "password"}
                className='flex-1 px-4 py-3 bg-transparent outline-none text-right'
                placeholder='ادخل كلمة المرور'
                dir='ltr'
              />
              <span className='px-2 text-xs text-gray-500'>
                <IoMdLock className='w-5 h-5' />
              </span>
            </div>
            {errors.password && (
              <p className='text-sm text-red-600 mt-1'>
                {errors.password.message}
              </p>
            )}
          </div>

          {/* Remember Me */}
          <div className='flex items-end justify-end mt-2'>
            <label className='flex items-center gap-2 cursor-pointer select-none'>
              <span className='text-gray-700'>تذكرني</span>
              <input
                type='checkbox'
                checked={rememberMe}
                onChange={() => setRememberMe(!rememberMe)}
                className='accent-red-700'
              />
            </label>
          </div>

          {/* Error Message */}
          {errorMsg && (
            <p className='text-sm text-red-600 text-center mt-2'>{errorMsg}</p>
          )}

          {/* Submit Button */}
          <button
            type='submit'
            disabled={loginMutation.isPending}
            className='w-full py-3 rounded-lg bg-red-700 hover:bg-red-800 text-white text-xl font-bold mt-2 transition-all duration-200 disabled:opacity-60'
          >
            {loginMutation.isPending ? "جاري التحميل..." : "تسجيل دخول"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Login;
